import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  BODY_TYPE_OPTIONS,
  DRIVER_CABIN_OPTIONS,
  FrontClearanceSelectOption,
  MANUFACTURER_OPTIONS,
  MASTER_MODEL_OPTIONS,
} from "../data";
import {
  selectFrontClearanceForm,
  selectFrontClearanceSaving,
  selectIsAddModalOpen,
} from "../redux/selector";
import { frontClearanceActions } from "../redux/slice";
import { FrontClearanceForm } from "../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof FrontClearanceForm;
  label: string;
  type: "select" | "text" | "textarea";
  options?: FrontClearanceSelectOption[];
  fullWidth?: boolean;
};

const FIELDS: FieldConfig[] = [
  {
    key: "manufacturer",
    label: "MANUFACTURER_NAME",
    type: "select",
    options: MANUFACTURER_OPTIONS,
  },
  {
    key: "masterModel",
    label: "MASTER_MODEL",
    type: "select",
    options: MASTER_MODEL_OPTIONS,
  },
  { key: "year", label: "YEAR", type: "text" },
  {
    key: "driverCabin",
    label: "DRIVER_CABIN",
    type: "select",
    options: DRIVER_CABIN_OPTIONS,
  },
  {
    key: "bodyType",
    label: "BODY_TYPE",
    type: "select",
    options: BODY_TYPE_OPTIONS,
    fullWidth: true,
  },
  { key: "optimalDistance", label: "OPTIMAL_DISTANCE", type: "text" },
  { key: "minimalDistance", label: "MINIMAL_DISTANCE", type: "text" },
  { key: "note", label: "NOTE", type: "textarea", fullWidth: true },
];

const REQUIRED_FIELDS: Array<keyof FrontClearanceForm> = [
  "manufacturer",
  "masterModel",
  "year",
  "driverCabin",
  "bodyType",
  "optimalDistance",
  "minimalDistance",
  "note",
];

const P = "FRONT_CLEARANCE.MODAL";

const AddFrontClearanceModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectFrontClearanceForm);
  const saving = useSelector(selectFrontClearanceSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(frontClearanceActions.closeAddModal());
  };

  const setField = (name: keyof FrontClearanceForm, value: string) =>
    dispatch(frontClearanceActions.setFormField({ name, value }));

  const placeholder = (label: string, type: "select" | "text" | "textarea") =>
    t(
      `${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`
    );

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const optionLabel = (option: FrontClearanceSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          <Box className={styles.grid}>
            {FIELDS.map(({ key, label, type, options = [], fullWidth }) => (
              <Box
                key={key}
                className={`${styles.field}${fullWidth ? ` ${styles.fullWidth}` : ""}`}
              >
                <Typography className={styles.label}>
                  {t(`${P}.FIELDS.${label}`)}
                </Typography>
                <TextField
                  className={
                    type === "textarea" ? styles.notesInput : styles.input
                  }
                  fullWidth
                  select={type === "select"}
                  multiline={type === "textarea"}
                  variant="outlined"
                  placeholder={placeholder(label, type)}
                  value={form[key]}
                  disabled={saving}
                  InputProps={{ notched: false }}
                  SelectProps={
                    type === "select"
                      ? {
                          displayEmpty: true,
                          IconComponent: DropdownIcon,
                          renderValue: (value) => {
                            const selected = options.find(
                              (option) => option.value === value
                            );
                            return selected
                              ? optionLabel(selected)
                              : placeholder(label, "select");
                          },
                        }
                      : undefined
                  }
                  onChange={(event) => setField(key, event.target.value)}
                >
                  {type === "select"
                    ? [
                        <MenuItem key="empty" value="" disabled>
                          {placeholder(label, "select")}
                        </MenuItem>,
                        ...options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {optionLabel(option)}
                          </MenuItem>
                        )),
                      ]
                    : undefined}
                </TextField>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(frontClearanceActions.addFrontClearance())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddFrontClearanceModal;
